/**
 * NitroPay native consent controls.
 *
 * NitroPay (once ads-XXXX.js has loaded) does NOT expose a generic "open privacy
 * settings" call. Instead it INJECTS its own region-appropriate control into a
 * placeholder element you place in the page:
 *
 *   - GDPR (EU/UK): a consent-management button injected into
 *     `<div id="ncmp-consent-link">`. Re-inject after SPA navigation with
 *     `__cmp('addConsentLink')` (or open the modal directly via
 *     `__cmp('showModal')`).
 *   - CCPA/US (California): a "Do Not Sell or Share" link injected into
 *     `<span data-ccpa-link="1">`. Re-inject with `__uspapi('addLink', 1)`.
 *
 * Both APIs only exist for users in the relevant region and only after the ad
 * script loads, so every call is guarded and is a no-op otherwise (e.g. a
 * non-EU, non-California visitor sees no injected control — which is correct).
 * Test from other regions with `?gdpr_debug=1` / `?usp_debug=1`.
 *
 * Docs:
 *   https://nitropay.com/docs/creating-a-link-for-the-user-to-manage-their-consents/
 *   https://nitropay.com/docs/us-privacy-law-compliance-steps/
 */

/** id NitroPay looks for to inject the GDPR consent-management button. */
export const NITRO_CONSENT_LINK_ID = 'ncmp-consent-link';

/**
 * Ask NitroPay to (re)inject its consent controls into the placeholder elements
 * on the current page — the GDPR button into `#ncmp-consent-link` and the CCPA
 * "Do Not Sell" link into `[data-ccpa-link]`. Safe to call repeatedly; a no-op
 * when the relevant CMP/USP API isn't present (script not loaded, or the user
 * isn't in a region that gets that control).
 */
export function injectConsentLinks() {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        if (typeof window.__cmp === 'function') {
            window.__cmp('addConsentLink');
        }
    } catch (e) {
        // CMP not ready or not in a GDPR region — nothing to inject.
    }
    try {
        if (typeof window.__uspapi === 'function') {
            window.__uspapi('addLink', 1);
        }
    } catch (e) {
        // USP not ready or not applicable — nothing to inject.
    }
}

/**
 * Open NitroPay's GDPR consent modal directly (EU/UK only). Returns true if the
 * CMP was available to handle it, false otherwise.
 */
export function openGdprConsentModal() {
    if (typeof window === 'undefined' || typeof window.__cmp !== 'function') {
        return false;
    }
    window.__cmp('showModal');
    return true;
}
